/**
 * Bundle Configurator
 * ---------------------------------------------------------------------------
 * Powers the "choose your items" UI on the Songwriters Under The Stars
 * bundle products. Lets a shopper pick which item + size/color fills each
 * slot in a bundle set, add multiple sets with different contents, and add
 * them all to the cart in one action while preserving the bundle product's
 * own quantity-break pricing (each set adds one line item of the bundle
 * product's own variant, so Shopify's native "Buy More, Save More" tiers,
 * which key off total quantity of that variant across the cart, still apply
 * no matter how many separate line items the sets are split into).
 *
 * Scoped entirely to elements with [data-bundle-configurator]; does nothing
 * on any other page.
 */
(function () {
  'use strict';

  // The six individual Songwriters Under The Stars items a bundle can be built from.
  var BUNDLE_ITEMS = [
    { key: 'tshirt', label: 'T-Shirt', handle: 'songwriters-under-the-stars-t-shirt' },
    { key: 'hoodie', label: 'Hoodie', handle: 'songwriters-under-the-stars-hoodie' },
    { key: 'longsleeve', label: 'Long Sleeve', handle: 'songwriters-under-the-stars-long-sleeve' },
    { key: 'ladiescut', label: 'Ladies Cut', handle: 'songwriters-under-the-stars-ladies-cut' },
    { key: 'ladiestank', label: 'Ladies Tank Top', handle: 'songwriters-under-the-stars-ladies-tank-top' },
    { key: 'hat', label: 'Hat', handle: 'songwriters-under-the-stars-hat' }
  ];

  var itemDataCache = null; // populated once via fetchItemData()
  var setCounter = 0;

  function fetchItemData() {
    if (itemDataCache) return Promise.resolve(itemDataCache);

    return Promise.all(
      BUNDLE_ITEMS.map(function (item) {
        return fetch('/products/' + item.handle + '.js')
          .then(function (res) {
            if (!res.ok) throw new Error('Failed to load ' + item.handle);
            return res.json();
          })
          .then(function (json) {
            var optionName = json.options_with_values && json.options_with_values[0]
              ? json.options_with_values[0].name
              : 'Option';
            return {
              key: item.key,
              label: item.label,
              handle: item.handle,
              optionName: optionName,
              variants: json.variants.map(function (v) {
                return { id: v.id, value: v.option1, available: v.available };
              }),
              image: json.featured_image || (json.images && json.images[0]) || null
            };
          })
          .catch(function (err) {
            console.error('[bundle-configurator]', err);
            return {
              key: item.key,
              label: item.label,
              handle: item.handle,
              optionName: 'Option',
              variants: [],
              image: null,
              loadFailed: true
            };
          });
      })
    ).then(function (results) {
      itemDataCache = results;
      return results;
    });
  }

  function itemByKey(key) {
    return itemDataCache.filter(function (i) { return i.key === key; })[0];
  }

  function buildSlotMarkup(setId, slotIndex) {
    var itemOptions = itemDataCache
      .map(function (i) {
        return '<option value="' + i.key + '">' + i.label + (i.loadFailed ? ' (unavailable)' : '') + '</option>';
      })
      .join('');

    return (
      '<div class="bundle_slot" data-bundle-slot data-slot-index="' + slotIndex + '">' +
        '<div class="bundle_slot_label">Item ' + (slotIndex + 1) + '</div>' +
        '<div class="bundle_slot_fields">' +
          '<select class="bundle_slot_item" data-bundle-item-select aria-label="Item ' + (slotIndex + 1) + ' - choose product">' +
            '<option value="" disabled selected>Choose item</option>' +
            itemOptions +
          '</select>' +
          '<select class="bundle_slot_option" data-bundle-option-select aria-label="Item ' + (slotIndex + 1) + ' - choose size or color" disabled>' +
            '<option value="" disabled selected>Choose item first</option>' +
          '</select>' +
        '</div>' +
      '</div>'
    );
  }

  function buildSetMarkup(setId, slots, setNumber) {
    var slotsHtml = '';
    for (var i = 0; i < slots; i++) {
      slotsHtml += buildSlotMarkup(setId, i);
    }
    return (
      '<div class="bundle_set" data-bundle-set data-set-id="' + setId + '">' +
        '<div class="bundle_set_header">' +
          '<span class="bundle_set_title">Set ' + setNumber + '</span>' +
          '<button type="button" class="bundle_set_remove" data-bundle-remove-set aria-label="Remove set ' + setNumber + '">&times;</button>' +
        '</div>' +
        '<div class="bundle_set_slots">' + slotsHtml + '</div>' +
      '</div>'
    );
  }

  function refreshOptionSelect(setEl, itemSelect) {
    var optionSelect = itemSelect.parentElement.querySelector('[data-bundle-option-select]');
    var itemKey = itemSelect.value;

    if (!itemKey) {
      optionSelect.innerHTML = '<option value="" disabled selected>Choose item first</option>';
      optionSelect.disabled = true;
      return;
    }

    var item = itemByKey(itemKey);
    if (!item || item.variants.length === 0) {
      optionSelect.innerHTML = '<option value="" disabled selected>Unavailable</option>';
      optionSelect.disabled = true;
      return;
    }

    var optLabel = item.optionName || 'Option';
    var optionsHtml = '<option value="" disabled selected>Choose ' + optLabel + '</option>';
    item.variants.forEach(function (v) {
      optionsHtml += '<option value="' + v.id + '" data-value="' + v.value + '"' + (v.available ? '' : ' disabled') + '>' +
        v.value + (v.available ? '' : ' (sold out)') + '</option>';
    });
    optionSelect.innerHTML = optionsHtml;
    optionSelect.disabled = false;
    optionSelect.setAttribute('aria-label', 'Choose ' + optLabel);
  }

  function collectSets(root) {
    var setEls = root.querySelectorAll('[data-bundle-set]');
    var sets = [];
    setEls.forEach(function (setEl) {
      var slots = [];
      setEl.querySelectorAll('[data-bundle-slot]').forEach(function (slotEl) {
        var itemSelect = slotEl.querySelector('[data-bundle-item-select]');
        var optionSelect = slotEl.querySelector('[data-bundle-option-select]');
        var itemKey = itemSelect.value;
        var item = itemKey ? itemByKey(itemKey) : null;
        var chosenOption = optionSelect.selectedOptions[0];
        slots.push({
          itemKey: itemKey,
          itemLabel: item ? item.label : '',
          variantId: optionSelect.value ? parseInt(optionSelect.value, 10) : null,
          optionValue: chosenOption ? chosenOption.getAttribute('data-value') : null,
          complete: !!(itemKey && optionSelect.value)
        });
      });
      sets.push({ slots: slots, complete: slots.length > 0 && slots.every(function (s) { return s.complete; }) });
    });
    return sets;
  }

  function updateSummaryAndValidity(root) {
    var sets = collectSets(root);
    var allComplete = sets.length > 0 && sets.every(function (s) { return s.complete; });
    var submitBtn = root.querySelector('[data-bundle-submit]');
    var summaryEl = root.querySelector('[data-bundle-summary]');
    var errorEl = root.querySelector('[data-bundle-error]');
    var setPrice = root.getAttribute('data-set-price');

    submitBtn.disabled = !allComplete;

    if (sets.length === 0) {
      summaryEl.innerHTML = '';
    } else {
      var totalRaw = parseFloat(root.getAttribute('data-set-price-raw')) * sets.length;
      summaryEl.innerHTML =
        '<span>' + sets.length + ' set' + (sets.length > 1 ? 's' : '') + ' &times; ' + setPrice + '</span>' +
        '<strong>' + (totalRaw / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) + '</strong>';
    }

    if (allComplete) {
      errorEl.setAttribute('hidden', 'hidden');
      errorEl.textContent = '';
    }

    return sets;
  }

  function addSet(root) {
    var slots = parseInt(root.getAttribute('data-slots'), 10) || 2;
    var setsWrapper = root.querySelector('[data-bundle-sets]');
    var setNumber = setsWrapper.querySelectorAll('[data-bundle-set]').length + 1;
    setCounter += 1;
    var setId = 'bset-' + setCounter;

    var wrapper = document.createElement('div');
    wrapper.innerHTML = buildSetMarkup(setId, slots, setNumber);
    var setEl = wrapper.firstElementChild;
    setsWrapper.appendChild(setEl);
    renumberSets(root);
    updateSummaryAndValidity(root);
  }

  function renumberSets(root) {
    var setsWrapper = root.querySelector('[data-bundle-sets]');
    var setEls = setsWrapper.querySelectorAll('[data-bundle-set]');
    setEls.forEach(function (setEl, idx) {
      setEl.querySelector('.bundle_set_title').textContent = 'Set ' + (idx + 1);
      setEl.querySelector('[data-bundle-remove-set]').setAttribute('aria-label', 'Remove set ' + (idx + 1));
    });
    // At least one set must always remain.
    var removeBtns = setsWrapper.querySelectorAll('[data-bundle-remove-set]');
    removeBtns.forEach(function (btn) {
      btn.style.visibility = setEls.length > 1 ? 'visible' : 'hidden';
    });
  }

  function submitBundle(root) {
    var sets = updateSummaryAndValidity(root);
    var allComplete = sets.length > 0 && sets.every(function (s) { return s.complete; });
    var errorEl = root.querySelector('[data-bundle-error]');
    var successEl = root.querySelector('[data-bundle-success]');
    var submitBtn = root.querySelector('[data-bundle-submit]');
    var loader = root.querySelector('[data-bundle-submit-loader]');

    if (!allComplete) {
      errorEl.textContent = 'Please choose an item and size/color for every slot in every set before adding to cart.';
      errorEl.removeAttribute('hidden');
      return;
    }

    var variantId = parseInt(root.getAttribute('data-variant-id'), 10);
    var items = sets.map(function (set) {
      var properties = {};
      set.slots.forEach(function (slot, idx) {
        properties['Item ' + (idx + 1)] = slot.itemLabel + ' - ' + slot.optionValue;
      });
      return { id: variantId, quantity: 1, properties: properties };
    });

    submitBtn.disabled = true;
    loader.classList.remove('hidden');
    errorEl.setAttribute('hidden', 'hidden');
    successEl.setAttribute('hidden', 'hidden');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(data.description || 'Could not add bundle to cart.');
          });
        }
        return res.json();
      })
      .then(function () {
        successEl.textContent = 'Added ' + items.length + ' set' + (items.length > 1 ? 's' : '') + ' to your cart! Redirecting...';
        successEl.removeAttribute('hidden');
        setTimeout(function () {
          window.location.href = '/cart';
        }, 900);
      })
      .catch(function (err) {
        errorEl.textContent = err.message || 'Something went wrong adding the bundle to your cart. Please try again.';
        errorEl.removeAttribute('hidden');
        submitBtn.disabled = false;
        loader.classList.add('hidden');
      });
  }

  function init(root) {
    fetchItemData().then(function () {
      addSet(root); // start with one set
    });

    root.addEventListener('click', function (e) {
      var addSetBtn = e.target.closest('[data-bundle-add-set]');
      if (addSetBtn) {
        addSet(root);
        return;
      }
      var removeSetBtn = e.target.closest('[data-bundle-remove-set]');
      if (removeSetBtn) {
        var setEl = removeSetBtn.closest('[data-bundle-set]');
        var totalSets = root.querySelectorAll('[data-bundle-set]').length;
        if (totalSets > 1 && setEl) {
          setEl.remove();
          renumberSets(root);
          updateSummaryAndValidity(root);
        }
        return;
      }
      var submitBtn = e.target.closest('[data-bundle-submit]');
      if (submitBtn && !submitBtn.disabled) {
        submitBundle(root);
      }
    });

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-bundle-item-select]')) {
        refreshOptionSelect(root, e.target);
        updateSummaryAndValidity(root);
      } else if (e.target.matches('[data-bundle-option-select]')) {
        updateSummaryAndValidity(root);
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-bundle-configurator]').forEach(function (root) {
      if (root.__bundleConfiguratorInit) return;
      root.__bundleConfiguratorInit = true;
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
